import { Export, Plus, DotsThreeVertical } from '@phosphor-icons/react';

/**
 * Platform-specific PWA install instructions.
 *
 * Designed as small inline visual mocks — chip-style numbered steps with
 * an icon hint where applicable, no real screenshots. Easy to replace
 * with real captures (GIF / mp4) by swapping the inner block per step.
 */

interface StepCardProps {
  number: number;
  title: string;
  Icon?: React.ComponentType<{ size?: number; weight?: 'bold' | 'regular' }>;
  iconBg?: string;
  description?: string;
}

function StepCard({ number, title, Icon, iconBg, description }: StepCardProps) {
  return (
    <li className="flex items-start gap-4 rounded-sm border border-neutral-200 bg-white px-4 py-3">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-neutral-900 text-xs font-medium text-white">
        {number}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-neutral-900">{title}</p>
          {Icon && (
            <span
              className={`inline-flex h-6 w-6 items-center justify-center rounded-sm ${
                iconBg ?? 'bg-neutral-100 text-neutral-700'
              }`}
            >
              <Icon size={14} weight="bold" />
            </span>
          )}
        </div>
        {description && (
          <p className="mt-1 text-xs text-neutral-500">{description}</p>
        )}
      </div>
    </li>
  );
}

export function IOSSafariInstall() {
  return (
    <ol className="space-y-2">
      <StepCard
        number={1}
        title="Tap the Share button"
        Icon={Export}
        iconBg="bg-blue-50 text-blue-600"
        description="In the bottom toolbar (or top right on iPad)."
      />
      <StepCard
        number={2}
        title="Scroll down in the share sheet"
      />
      <StepCard
        number={3}
        title="Tap 'Add to Home Screen'"
        Icon={Plus}
        iconBg="bg-neutral-900 text-white"
      />
      <StepCard
        number={4}
        title="Tap 'Add' (top right)"
        description="Solem now opens as its own app from your home screen."
      />
    </ol>
  );
}

export function AndroidChromeInstall() {
  return (
    <ol className="space-y-2">
      <StepCard
        number={1}
        title="Tap the menu (⋮)"
        Icon={DotsThreeVertical}
        iconBg="bg-neutral-100 text-neutral-700"
        description="Top-right corner in Chrome."
      />
      <StepCard
        number={2}
        title="Tap 'Install app'"
        description="Or 'Add to Home screen' on older Chrome versions."
      />
      <StepCard
        number={3}
        title="Tap 'Install' to confirm"
        description="Solem now opens as its own app from your home screen."
      />
    </ol>
  );
}

export function IOSNonSafariCallout() {
  return (
    <div className="rounded-sm border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
      <p className="font-medium">Open this page in Safari first.</p>
      <p className="mt-1 text-amber-800/80">
        iOS only lets Safari install apps to your home screen. Tap the share
        menu in this browser and choose <em>Open in Safari</em>.
      </p>
    </div>
  );
}
